import React, { createContext, useContext, useState, useCallback } from 'react';

const CommunityContext = createContext();

const STORAGE_KEY = 'caisar-community-hooks';
const DISCUSSIONS_KEY = 'caisar-community-user-discussions';
const REACTIONS_KEY = 'caisar-community-reactions';

function generateId() {
  return Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
}

function loadFromStorage(key, defaultValue) {
  try {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : defaultValue;
  } catch { return defaultValue; }
}

function saveToStorage(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
}

function loadHooks() {
  return loadFromStorage(STORAGE_KEY, []);
}

function persistHooks(hooks) {
  saveToStorage(STORAGE_KEY, hooks);
}

// ── Simulierte Diskussionen für die Mock-Beiträge ─────────────────────────────

const MOCK_DISCUSSIONS = {
  'mock-1': [
    {
      id: 'm1d1', author: 'Dr. Klaus Berger', initials: 'KB',
      text: 'Sehr gut dokumentierter Fall. Zur Frage Thrombolyse vs. PCI: Nach ESC-Leitlinie 2023 ist die primäre PCI immer bevorzugt, wenn das Katheterzentrum innerhalb von 120 min erreichbar ist. Thrombolyse bleibt die Brücke, wenn dieses Zeitfenster nicht einzuhalten ist.',
      postedAt: '2026-02-18T15:10:00.000Z',
      reactions: { helpful: 12, insightful: 8, debatable: 0 },
    },
    {
      id: 'm1d2', author: 'Dr. Sandra Klein', initials: 'SK',
      text: 'Wichtiger Zusatz zur Antikoagulation bei gleichzeitigem VHF: Triple-Therapie (OAK + ASS + P2Y12) maximal 4 Wochen, dann Wechsel auf duale Therapie (OAK + Clopidogrel). HAS-BLED-Score zur Risikoabschätzung nicht vergessen!',
      postedAt: '2026-02-18T16:35:00.000Z',
      reactions: { helpful: 9, insightful: 14, debatable: 1 },
    },
    {
      id: 'm1d3', author: 'Dr. Ralf Zimmermann', initials: 'RZ',
      text: 'Danke für die Erläuterung! Ich hatte einen ähnlichen Fall, bei dem VHF erst postinterventionell auftrat. Mein Kardiologenkollege hat ASS nach 4 Wochen abgesetzt und auf OAK + Clopidogrel gewechselt – genau das, was Sie beschreiben.',
      postedAt: '2026-02-18T18:05:00.000Z',
      reactions: { helpful: 5, insightful: 4, debatable: 0 },
    },
    {
      id: 'm1d4', author: 'Dr. Klaus Berger', initials: 'KB',
      text: 'Korrekt. Das 2023-ESC-Update empfiehlt nach 1–4 Wochen Dreifachtherapie den Wechsel auf OAK + P2Y12 (Clopidogrel bevorzugt vor Ticagrelor wegen niedrigerem Blutungsrisiko). CHA₂DS₂-VASc-Score bestimmt dabei die Dauer der oralen Antikoagulation.',
      postedAt: '2026-02-18T19:20:00.000Z',
      reactions: { helpful: 11, insightful: 7, debatable: 0 },
    },
    {
      id: 'm1d5', author: 'Dr. Sandra Klein', initials: 'SK',
      text: 'Wurde eine LVEF gemessen? Bei anteriorem STEMI mit V1–V4-Beteiligung frühzeitig an ACE-Hemmer und ICD-Indikation nach 40 Tagen denken (LVEF <35 %). Das sollte in der Entlassung explizit dokumentiert sein.',
      postedAt: '2026-02-19T08:15:00.000Z',
      reactions: { helpful: 8, insightful: 15, debatable: 1 },
    },
    {
      id: 'm1d6', author: 'Dr. Anna Müller', initials: 'AM',
      text: 'LVEF lag post-PCI bei 42 %. ACE-Hemmer und Beta-Blocker wurden eingeleitet. ICD-Re-Evaluation nach 3 Monaten geplant, da LVEF noch im Grenzbereich. Herzlichen Dank für die wertvollen Hinweise!',
      postedAt: '2026-02-19T09:45:00.000Z',
      reactions: { helpful: 7, insightful: 5, debatable: 0 },
    },
    {
      id: 'm1d7', author: 'Dr. Ralf Zimmermann', initials: 'RZ',
      text: 'Wie erklärt ihr das Blutungsrisiko unter Triple-Therapie dem Patienten verständlich? Mir fällt es schwer, das Balance-Prinzip ohne Fachsprache zu vermitteln.',
      postedAt: '2026-02-19T11:30:00.000Z',
      reactions: { helpful: 6, insightful: 2, debatable: 0 },
    },
    {
      id: 'm1d8', author: 'Dr. Klaus Berger', initials: 'KB',
      text: 'Ich nutze folgende Analogie: „Wir verdünnen das Blut, damit keine neuen Gerinnsel entstehen – das schützt das Herz, aber Wunden bluten etwas länger." Schriftliche Warnzeichen (Schwarzstuhl, Blut im Urin, spontane Hämatome) immer mitgeben.',
      postedAt: '2026-02-19T13:00:00.000Z',
      reactions: { helpful: 14, insightful: 9, debatable: 0 },
    },
  ],
  'mock-2': [
    {
      id: 'm2d1', author: 'Dr. Christian Bauer', initials: 'CB',
      text: 'Typischer S1-Radikulopathie-Fall. Laut AWMF-Leitlinie 033-048 sind 6–8 Wochen konservative Therapie angezeigt, sofern keine Cauda-equina-Symptomatik vorliegt. Nur ca. 10–15 % der Patienten benötigen letztlich eine Operation – das sollte dem Patienten früh kommuniziert werden.',
      postedAt: '2026-02-17T10:00:00.000Z',
      reactions: { helpful: 10, insightful: 11, debatable: 1 },
    },
    {
      id: 'm2d2', author: 'Dr. Sandra Klein', initials: 'SK',
      text: 'Ich empfehle die Kombination aus Physiotherapie, NSAR und Stufenlagerung. Pregabalin 75 mg ist beim neuropathischen Anteil sinnvoll. Wichtig: Frühzeitig psychosoziale Yellow Flags erheben – Angst-Vermeidungs-Überzeugungen sind ein starker Chronifizierungsfaktor.',
      postedAt: '2026-02-17T11:30:00.000Z',
      reactions: { helpful: 9, insightful: 13, debatable: 0 },
    },
    {
      id: 'm2d3', author: 'Dr. Thomas Weber', initials: 'TW',
      text: 'Danke für die Hinweise. Zur PRT: Bei uns liegt die Erfolgsrate (OP-Vermeidung) nach CT-gesteuerter PRT bei ca. 60–70 %. Wir warten in der Regel 4–6 Wochen konservative Therapie ab, bevor wir die PRT anbieten.',
      postedAt: '2026-02-17T13:15:00.000Z',
      reactions: { helpful: 7, insightful: 8, debatable: 2 },
    },
    {
      id: 'm2d4', author: 'Dr. Christian Bauer', initials: 'CB',
      text: 'Die 60–70 % sind realistisch. Zur Selektion: Motorisches Defizit (KG < 3) ist für mich eine Indikation zur zeitnahen OP. Rein radikuläre Schmerzen ohne Motordefizit – da bin ich geduldig mit konservativem Vorgehen.',
      postedAt: '2026-02-17T15:00:00.000Z',
      reactions: { helpful: 12, insightful: 8, debatable: 0 },
    },
    {
      id: 'm2d5', author: 'Dr. Sandra Klein', initials: 'SK',
      text: 'Yellow Flags konkret: Hat der Patient ausgeprägte Kinesiephobie? Das würde ich mit der Tampa Scale (TSK) screenen. Außerdem: Selbständigkeit mit hohem Arbeitsdruck kann die Heilung nachweislich verlangsamen.',
      postedAt: '2026-02-18T08:30:00.000Z',
      reactions: { helpful: 9, insightful: 14, debatable: 0 },
    },
    {
      id: 'm2d6', author: 'Dr. Thomas Weber', initials: 'TW',
      text: 'Guter Punkt – ein formelles Yellow-Flag-Screening hatte ich nicht durchgeführt. Der Patient ist tatsächlich selbständig und unter erheblichem Arbeitsdruck. Ich werde beim nächsten Termin den TSK einsetzen.',
      postedAt: '2026-02-18T10:00:00.000Z',
      reactions: { helpful: 5, insightful: 4, debatable: 0 },
    },
    {
      id: 'm2d7', author: 'Dr. Christian Bauer', initials: 'CB',
      text: 'Zur Frage PRT vs. epidurale Injektion: Ich bevorzuge die foraminale PRT unter CT-Kontrolle – sie ist zielgenauer und ermöglicht eine gezielte Steroiddeponierung am Nervenwurzelursprung. Epidurale Injektion interlaminar ist technisch einfacher, aber weniger selektiv. Evidenzlevel für beide: Ib.',
      postedAt: '2026-02-18T14:30:00.000Z',
      reactions: { helpful: 11, insightful: 10, debatable: 3 },
    },
  ],
  // Generische Demo-Diskussion für eigene geteilte Beiträge
  '__user__': [
    {
      id: 'ud1', author: 'Dr. Klaus Berger', initials: 'KB',
      text: 'Danke für das Teilen dieses Falls – gut strukturiert dokumentiert. Haben Sie in ähnlichen Konstellationen bereits Erfahrungen gemacht, die Sie ergänzen möchten?',
      postedAt: '2026-07-10T09:15:00.000Z',
      reactions: { helpful: 3, insightful: 2, debatable: 0 },
    },
    {
      id: 'ud2', author: 'Dr. Sandra Klein', initials: 'SK',
      text: 'Schlüssig dokumentierter Fall. Ist die Abklärung bereits abgeschlossen, oder sind noch weitere Diagnostikschritte geplant? Würde gerne den Verlauf erfahren.',
      postedAt: '2026-07-10T11:40:00.000Z',
      reactions: { helpful: 4, insightful: 1, debatable: 0 },
    },
    {
      id: 'ud3', author: 'Dr. Anna Müller', initials: 'AM',
      text: 'Interessante Befundkonstellation! Laut unserer Leitlinie wird in ähnlichen Fällen eine engmaschige Verlaufskontrolle empfohlen. Wie haben Sie sich letztlich entschieden?',
      postedAt: '2026-07-11T08:00:00.000Z',
      reactions: { helpful: 2, insightful: 3, debatable: 1 },
    },
  ],
};

const MOCK_COMMUNITY_HOOKS = [
  {
    id: 'mock-1',
    isMock: true,
    author: 'Dr. Anna Müller',
    name: 'STEMI – Brustschmerz, Armausstrahlung – Thrombolyse vs. PCI?',
    summary: [
      { title: 'Aktuelle Beschwerden', items: ['Stechender Brustschmerz seit 2 Stunden', 'Ausstrahlung in linken Arm', 'Begleitende Übelkeit und Schweißausbrüche'] },
      { title: 'Vorerkrankungen', items: ['Arterielle Hypertonie seit 8 Jahren', 'Hypercholesterinämie', 'Familienanamnese: Vater Myokardinfarkt mit 55'] },
      { title: 'Durchgeführte Untersuchungen', items: ['EKG: ST-Hebungen in V1-V4', 'Troponin T erhöht: 0.8 ng/ml', 'RR 160/95 mmHg, HF 98/min'] },
    ],
    diagnoses: [
      { name: 'Akuter ST-Hebungsinfarkt (STEMI)', reason: 'Typische Klinik mit ST-Hebungen und Troponinerhöhung', diagnostics: ['Herzkatheter notfallmäßig'], treatment: ['Primäre PCI'], medications: ['ASS 500mg i.v.', 'Heparin 5000 IE', 'Ticagrelor 180mg'] },
      { name: 'Instabile Angina pectoris', reason: 'Differentialdiagnose bei akutem Koronarsyndrom', diagnostics: ['Serielle Troponinbestimmung'], treatment: ['Monitoring auf Chest Pain Unit'], medications: ['Nitroglycerin sublingual'] },
    ],
    manualNotes: 'Patient sofort in Herzkatheter-Bereitschaft. Kardiologie informiert.',
    discussionPoints: 'Wann ist eine Thrombolyse der PCI vorzuziehen? Welche Antikoagulation bei gleichzeitigem Vorhofflimmern?',
    sharedAt: '2026-02-18T14:30:00.000Z',
    views: 87,
  },
  {
    id: 'mock-2',
    isMock: true,
    author: 'Dr. Thomas Weber',
    name: 'Bandscheibenvorfall L5/S1 – Rückenschmerzen, Beinausstrahlung – Konservativ vs. OP?',
    summary: [
      { title: 'Aktuelle Beschwerden', items: ['Rückenschmerzen seit 6 Wochen', 'Ausstrahlung ins rechte Bein bis zum Fuß', 'Taubheitsgefühl an der Außenseite des rechten Unterschenkels'] },
      { title: 'Begleitsymptome', items: ['Verstärkung beim Sitzen und Vornüberbeugen', 'Nächtliches Erwachen durch Schmerzen', 'Keine Blasen- oder Mastdarmstörungen'] },
      { title: 'Aktuelle Medikation', items: ['Ibuprofen 600mg bei Bedarf', 'Physiotherapie 2x/Woche seit 3 Wochen'] },
    ],
    diagnoses: [
      { name: 'Bandscheibenvorfall L5/S1 rechts', reason: 'Typische radikulopathische Symptomatik im Dermatom S1', diagnostics: ['MRT der LWS'], treatment: ['Konservative Therapie fortführen', 'CT-gesteuerte PRT bei ausbleibendem Erfolg'], medications: ['Pregabalin 75mg 1-0-1', 'Ibuprofen 600mg 1-1-1'] },
      { name: 'Spinalkanalstenose', reason: 'Differentialdiagnose bei älteren Patienten mit Claudicatio spinalis', diagnostics: ['MRT mit Kontrastmittel'], treatment: ['Laminektomie bei Versagen konservativer Therapie'], medications: ['Gabapentin als Alternative'] },
      { name: 'Piriformis-Syndrom', reason: 'Pseudoradikulopathie durch Kompression des N. ischiadicus', diagnostics: ['Klinischer FAIR-Test', 'Sono M. piriformis'], treatment: ['Gezielte Dehnungsübungen', 'Triggerpunkttherapie'], medications: ['Muskelrelaxans bei Bedarf'] },
    ],
    manualNotes: '',
    discussionPoints: 'Wie lange konservativ behandeln bevor OP-Indikation? Erfahrungen mit PRT vs. epiduraler Injektion?',
    sharedAt: '2026-02-17T09:15:00.000Z',
    views: 42,
  },
  {
    id: 'mock-3',
    isMock: true,
    author: 'Dr. Sarah Fischer',
    name: 'COPD – Belastungsdyspnoe, Reizhusten – COPD vs. Asthma differenzieren?',
    summary: [
      { title: 'Aktuelle Beschwerden', items: ['Zunehmende Atemnot bei Belastung seit 3 Monaten', 'Trockener Reizhusten, besonders nachts', 'Verminderte Belastbarkeit beim Treppensteigen'] },
      { title: 'Relevante Lebensumstände', items: ['Raucher seit 25 Jahren, 20 pack-years', 'Büroarbeit, wenig körperliche Aktivität'] },
      { title: 'Verordnete Medikation', items: ['Salbutamol DA 2 Hübe bei Bedarf', 'Budesonid/Formoterol 160/4.5 µg 1-0-1'] },
    ],
    diagnoses: [
      { name: 'COPD Stadium II (GOLD)', reason: 'Langjähriger Nikotinabusus mit progredienter Belastungsdyspnoe', diagnostics: ['Lungenfunktionstest', 'Röntgen-Thorax', 'BGA'], treatment: ['Rauchstopp-Beratung', 'Pneumologische Rehabilitation'], medications: ['Tiotropium 18µg 1x täglich', 'Salbutamol bei Bedarf'] },
      { name: 'Asthma bronchiale', reason: 'Nächtlicher Husten und Reversibilität möglich', diagnostics: ['Methacholin-Provokationstest', 'Allergiediagnostik'], treatment: ['Stufentherapie nach GINA'], medications: ['ICS/LABA Kombination'] },
    ],
    manualNotes: 'Rauchstopp dringend empfohlen. Kontrolle Lungenfunktion in 4 Wochen.',
    discussionPoints: 'COPD vs. Asthma bei Rauchern – wie sicher differenzieren? Wann früh inhalative Therapie beginnen?',
    sharedAt: '2026-02-16T16:45:00.000Z',
    views: 118,
  },
  {
    id: 'mock-4',
    isMock: true,
    author: 'Dr. Michael Schmidt',
    name: 'Migräne mit Aura – Kopfschmerzen, Flimmerskotom – Propranolol vs. Topiramat?',
    summary: [
      { title: 'Aktuelle Beschwerden', items: ['Heftige halbseitige Kopfschmerzen seit heute Morgen', 'Vorangegangene Sehstörungen (Flimmerskotom) für ca. 20 Minuten', 'Begleitende Photophobie und Phonophobie'] },
      { title: 'Begleitsymptome', items: ['Übelkeit ohne Erbrechen', 'Kribbeln in der rechten Hand (ca. 15 Minuten)', 'Anfälle ca. 2-3x pro Monat seit dem Jugendalter'] },
    ],
    diagnoses: [
      { name: 'Migräne mit Aura', reason: 'Typische visuelle Aura gefolgt von halbseitigem Kopfschmerz', diagnostics: ['Neurologische Untersuchung', 'ggf. cMRT zum Ausschluss struktureller Ursachen'], treatment: ['Akuttherapie und Prophylaxe', 'Migräne-Tagebuch führen'], medications: ['Sumatriptan 50mg bei Attacke', 'Propranolol 40mg 1-0-1 zur Prophylaxe'] },
      { name: 'Spannungskopfschmerz', reason: 'Häufige Differentialdiagnose, episodischer Charakter', diagnostics: ['Kopfschmerz-Tagebuch'], treatment: ['Stressreduktion', 'Progressive Muskelrelaxation'], medications: ['Paracetamol 1g bei Bedarf'] },
    ],
    manualNotes: 'Patientin führt bereits Kopfschmerzkalender. Überweisung Neurologie zur Prophylaxe-Einstellung.',
    discussionPoints: 'Propranolol vs. Topiramat als Erstlinien-Prophylaxe? Ab welcher Attackenfrequenz Prophylaxe starten?',
    sharedAt: '2026-02-15T11:20:00.000Z',
    views: 63,
  },
  {
    id: 'mock-5',
    isMock: true,
    author: 'Dr. Lisa Hoffmann',
    name: 'Appendizitis – Unterbauchschmerzen, Schmerzwanderung – Alvarado-Score ausreichend?',
    summary: [
      { title: 'Aktuelle Beschwerden', items: ['Seit gestern Abend zunehmende Schmerzen im rechten Unterbauch', 'Initial periumbilikaler Schmerz, dann Wanderung nach rechts unten', 'Appetitlosigkeit und einmaliges Erbrechen'] },
      { title: 'Durchgeführte Untersuchungen', items: ['McBurney-Punkt druckschmerzhaft', 'Loslassschmerz positiv', 'Leukozyten 14.200/µl, CRP 45 mg/l', 'Sonografie: Kokarde 9mm, freie Flüssigkeit periappendizeäl'] },
      { title: 'Geplante Diagnostik', items: ['CT Abdomen bei unklarem Befund', 'Urinstatus zum Ausschluss Harnwegsinfekt'] },
    ],
    diagnoses: [
      { name: 'Akute Appendizitis', reason: 'Typische Schmerzwanderung, positiver McBurney, Leukozytose und sonografische Kokarde', diagnostics: ['OP-Freigabe einholen', 'Anästhesie-Aufklärung'], treatment: ['Laparoskopische Appendektomie'], medications: ['Metamizol 1g i.v.', 'Cefuroxim 1.5g i.v. perioperativ'] },
      { name: 'Mesenteriale Lymphadenitis', reason: 'Kann klinisch ähnlich imponieren, häufig viral bedingt', diagnostics: ['CT zur Differenzierung'], treatment: ['Konservativ, Beobachtung'], medications: ['Analgesie nach Bedarf'] },
    ],
    manualNotes: 'Chirurgisches Konsil angefordert. Patient nüchtern lassen für mögliche OP.',
    discussionPoints: 'Alvarado-Score zur OP-Entscheidung ausreichend? Wann CT statt Sono bei Verdacht auf Appendizitis?',
    sharedAt: '2026-02-14T08:00:00.000Z',
    views: 95,
  },
];

export function CommunityProvider({ children }) {
  const [hooks, setHooks] = useState(loadHooks);
  const [userDiscussions, setUserDiscussions] = useState(() => loadFromStorage(DISCUSSIONS_KEY, {}));
  const [userReactions, setUserReactions] = useState(() => loadFromStorage(REACTIONS_KEY, {}));

  const shareHook = useCallback((sessionData, discussionPoints, hookName) => {
    setHooks((prev) => {
      const hook = {
        id: generateId(),
        sessionId: sessionData.id,
        name: hookName || sessionData.name,
        summary: sessionData.summary || null,
        diagnoses: sessionData.diagnoses || null,
        manualNotes: sessionData.manualNotes || '',
        discussionPoints: discussionPoints || '',
        sharedAt: new Date().toISOString(),
        views: 0,
      };
      const next = [hook, ...prev];
      persistHooks(next);
      return next;
    });
  }, []);

  const removeHook = useCallback((id) => {
    setHooks((prev) => {
      const next = prev.filter((h) => h.id !== id);
      persistHooks(next);
      return next;
    });
  }, []);

  const getMyHooks = useCallback(() => {
    return hooks.sort((a, b) => new Date(b.sharedAt) - new Date(a.sharedAt));
  }, [hooks]);

  const isShared = useCallback((sessionId) => {
    return hooks.some((h) => h.sessionId === sessionId);
  }, [hooks]);

  const incrementViews = useCallback((id) => {
    setHooks((prev) => {
      const next = prev.map((h) => (h.id === id ? { ...h, views: (h.views || 0) + 1 } : h));
      persistHooks(next);
      return next;
    });
  }, []);

  const getAllCommunityHooks = useCallback(() => {
    const all = [
      ...hooks.map((h) => ({ ...h, author: 'Du', isMock: false })),
      ...MOCK_COMMUNITY_HOOKS,
    ];
    return all.sort((a, b) => new Date(b.sharedAt) - new Date(a.sharedAt));
  }, [hooks]);

  const getMostReadHooks = useCallback(() => {
    const all = [
      ...hooks.map((h) => ({ ...h, author: 'Du', isMock: false })),
      ...MOCK_COMMUNITY_HOOKS,
    ];
    return all.sort((a, b) => (b.views || 0) - (a.views || 0));
  }, [hooks]);

  const getRecentHooks = useCallback(() => {
    const all = [
      ...hooks.map((h) => ({ ...h, author: 'Du', isMock: false })),
      ...MOCK_COMMUNITY_HOOKS,
    ];
    return all.sort((a, b) => new Date(b.sharedAt) - new Date(a.sharedAt));
  }, [hooks]);

  // ── Diskussion ────────────────────────────────────────────────────────────────

  const getDiscussions = useCallback((hookId, isMock) => {
    const seeded = isMock
      ? (MOCK_DISCUSSIONS[hookId] || [])
      : (MOCK_DISCUSSIONS['__user__'] || []);
    const added = userDiscussions[hookId] || [];
    return [...seeded, ...added].sort((a, b) => new Date(a.postedAt) - new Date(b.postedAt));
  }, [userDiscussions]);

  const addComment = useCallback((hookId, text) => {
    const comment = {
      id: generateId(),
      author: 'Du',
      initials: 'Du',
      isOwn: true,
      text,
      postedAt: new Date().toISOString(),
      reactions: { helpful: 0, insightful: 0, debatable: 0 },
    };
    setUserDiscussions(prev => {
      const next = { ...prev, [hookId]: [...(prev[hookId] || []), comment] };
      saveToStorage(DISCUSSIONS_KEY, next);
      return next;
    });
  }, []);

  const toggleReaction = useCallback((commentId, reactionType) => {
    setUserReactions(prev => {
      const next = { ...prev };
      if (next[commentId] === reactionType) {
        delete next[commentId];
      } else {
        next[commentId] = reactionType;
      }
      saveToStorage(REACTIONS_KEY, next);
      return next;
    });
  }, []);

  return (
    <CommunityContext.Provider value={{
      shareHook,
      removeHook,
      getMyHooks,
      isShared,
      incrementViews,
      getAllCommunityHooks,
      getMostReadHooks,
      getRecentHooks,
      MOCK_COMMUNITY_HOOKS,
      getDiscussions,
      addComment,
      toggleReaction,
      userReactions,
    }}>
      {children}
    </CommunityContext.Provider>
  );
}

export function useCommunity() {
  return useContext(CommunityContext);
}
